import { beginRequestFeedback, endRequestFeedback } from '../components/common/NavigationFeedback/controller'
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from 'axios'
import Cookies from 'js-cookie'
import { APP_ROUTES_LANDING, APP_ROUTES_LOGIN } from '../constants'
import {
  authHeaderKeys,
  CustomAxiosResponse,
  persistAuthHeadersInDeviceCookies,
  persistAuthHeadersInDeviceStorage,
  setAuthHeaders
} from '../utils/auth'
import { Storage } from './auth'

const BASE_API_URL = process.env.REACT_APP_API_URL

// Create an Axios instance
export const AXIOS_INSTANCE = axios.create({
  baseURL: `${BASE_API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': '69420'
  }
})

function isGetRequest(method?: Method | string) {
  return (method ?? 'get').toLowerCase() === 'get'
}

function shouldTrackPageLoadFeedback(config: AxiosRequestConfig) {
  return isGetRequest(config.method) && !config.skipNavigationFeedback
}

function endTrackedRequestFeedback(config?: AxiosRequestConfig) {
  if (!config?.__navigationFeedbackTracked) return
  config.__navigationFeedbackTracked = false
  endRequestFeedback()
}

// Add an interceptor to include the token
AXIOS_INSTANCE.interceptors.request.use(
  config => {
    // Public endpoints (e.g. patient registration forms) must not send staff auth headers
    if (!config.skipAuth) {
      const token = Cookies.get(authHeaderKeys[0])
      if (token) {
        authHeaderKeys.forEach((key: string) => {
          config.headers[key] = Cookies.get(key)
        })
      }
    }

    if (shouldTrackPageLoadFeedback(config)) {
      beginRequestFeedback()
      config.__navigationFeedbackTracked = true
    }

    return config
  },
  error => {
    endTrackedRequestFeedback(error?.config)
    return Promise.reject(error)
  }
)

const onResponseSuccess = (response: AxiosResponse) => {
  endTrackedRequestFeedback(response.config)
  const customResponse = response as CustomAxiosResponse
  if (response.config.skipAuthHeaderPersist) {
    return response
  }
  if (
    customResponse.headers &&
    customResponse.headers['access-token'] &&
    customResponse.headers.client &&
    customResponse.headers['token-type']
  ) {
    // console.log('customResponse.headers', customResponse.headers)
    setAuthHeaders(customResponse.headers)
    persistAuthHeadersInDeviceStorage(Storage, customResponse.headers)
    persistAuthHeadersInDeviceCookies(customResponse.headers)
  }
  return response
}
function onResponseError(err: AxiosError) {
  endTrackedRequestFeedback(err.config)
  const status = err.status || (err.response ? err.response.status : 0)
  const currentURL = window.location.href.toString().split(window.location.host)[1]
  const unauthorisedPages = [
    APP_ROUTES_LOGIN,
    '/forgot-password',
    '/reset-password',
    '/unlock-account',
    '/invitation/accept?',
    '/register/',
    APP_ROUTES_LANDING
  ]
  if (
    status === 401 &&
    !unauthorisedPages.some(
      page =>
        currentURL.startsWith(page) || (page === '/invitation/accept' && currentURL.startsWith('/invitation/accept?'))
    )
  ) {
    // Save current URL before clearing storage
    const currentPath = window.location.pathname + window.location.search
    if (
      currentPath !== APP_ROUTES_LOGIN &&
      !currentPath.startsWith('/forgot-password') &&
      !currentPath.startsWith('/reset-password') &&
      !currentPath.startsWith('/unlock-account')
    ) {
      sessionStorage.setItem('redirectAfterLogin', currentPath)
    }
    Cookies.remove('token')
    localStorage.clear()
    // Preserve redirectAfterLogin in sessionStorage
    const redirectUrl = sessionStorage.getItem('redirectAfterLogin')
    sessionStorage.clear()
    if (redirectUrl) {
      sessionStorage.setItem('redirectAfterLogin', redirectUrl)
    }
    window.location.href = APP_ROUTES_LOGIN
  }

  return Promise.reject(err)
}
AXIOS_INSTANCE.interceptors.response.use(onResponseSuccess, onResponseError)

export default AXIOS_INSTANCE
